import type { IBatchStore } from './batch-store-types';
import logger from '../../utils/logger';
import { timerKey } from '../../utils/timer-manager/timer-manager';
import { DeviceStatus } from '../../constants/DeviceStatus';
import { toNumber } from '../../utils/numbers';
import { ScaleTypes } from '../discharge-store';
import { DischargeAmountType } from '../../containers/Discharge/DischargeAmountType';
import { discharge as _discharge } from './discharge-operations/discharge-control';
import {
  getSecondWaterPump,
  getWaterPump,
  isHeadWaterNotDischarged,
} from './water-pump-checks';
import { reaction } from 'mobx';
import {
  DoubleGatesOpenMode,
  SecondGateOpenDelayUnit,
} from '../../constants/DeviceModels';
import type { IBatchData } from '../../containers/Batch/IBatchData';
import { BatchCycleStatus } from '../../constants/BatchCycleStatus';
import { BatchAction } from '../../constants/MaterialModels';
import type { IScale } from '../../../types/device/DeviceModels';
import type {
  IMaterial,
  IWeighedMaterialModel,
} from '../../../types/material/MaterialModels';
import type { DischargeScale } from '../../../types/api/Discharge';
import type { IWeighedDataToBatch } from '../../../types/batch/DataToBatch';
import { MixingStartPoint } from '../../constants/BatchTypeEnums';

export const dischargeHelpersMixin = {

  dischargeWithSequence(this: IBatchStore, runningData: IBatchData) {

    if (this.isAbort || !this.isActiveDrop(runningData)) {
      return;
    }

    this.context.onBatchCycle(BatchCycleStatus.START_DISCHARGING, this.inputs, this.outputs, this.channels);

    if (!runningData.finalScales) {
      logger.warn('No final scales for discharge!', runningData);

      if (!this.areAllPumpsFinished(runningData)) {
        Object.keys(BatchAction).forEach(action => {
          this.onBatchAction(runningData, action);
        });
      }

      this.checkPumpsFinish(runningData);
      return;
    }

    runningData.sequence.discharges = runningData.sequence.discharges
      .filter((v: DischargeScale) => runningData.finalScales[v.scaleId]);
    runningData.sequence.discharges
      .forEach((scale: DischargeScale, i: number) => {
        runningData.sequence.discharges[i] = { ...scale, ...this.devicesStore.getDeviceById(scale.scaleId) };
      });

    const isOneScale = Object.keys(runningData.finalScales).length === 1;
    const primaries = runningData.sequence.discharges.filter((v: DischargeScale) => v.isPrimary);
    let primary = primaries.find((v: DischargeScale) => v.order === 1) || primaries[0];
    let secondaries = runningData.sequence.discharges.filter((v: DischargeScale) => !v.isPrimary);
    const isPrimaryDischarged = primary
      && (runningData.finalScalesDischarged.includes(primary.id) || primary.dischargeFinish);
    const waterScale = runningData.sequence.discharges.find((v: DischargeScale) => !v.dischargeFinish && v.type === ScaleTypes.WATER);
    const isOvercapacityTarget = !!(
      waterScale &&
      runningData?.scales?.[waterScale.id]?.some((v: any) => v.overcapacityTarget)
    );
    const isExtraWater = !!(
      waterScale &&
      runningData?.scales?.[waterScale.id]?.some((v: any) => v.extraWater)
    );

    logger.log(`using discharge sequence: ${runningData.sequence.id}`, 'runningData', runningData,
      'primary', primary, 'head water', runningData.headWater, 'wash water', runningData.washWater,
      'isPrimaryDischarged', isPrimaryDischarged, 'isOvercapacityTarget:', isOvercapacityTarget,
      'isExtraWater', isExtraWater);

    if (isPrimaryDischarged && (isOvercapacityTarget || isExtraWater)) {
      logger.log('discharge water scale, because primary discharged', waterScale.id);

      const ps = this.getDeviceStatus(waterScale.id);
      logger.log(`checking drop & uid, scale #${waterScale.id}`, ps.currentDrop, runningData.currentDrop, ps.uid, runningData.uid);

      if (ps.uid === runningData.uid && ps.currentDrop === runningData.currentDrop) {
        this.dischargeScale({ ...waterScale, isPrimary: true }, runningData);
      }

      return;
    }

    if (isOneScale) {
      const scaleId = Number(Object.keys(runningData.finalScales)[0]);
      primary = runningData.sequence.discharges.find((v: DischargeScale) => v.scaleId === scaleId);
      logger.log('discharge one scale only', primary.id);

      const ps = this.getDeviceStatus(primary.id);
      logger.log(`checking drop & uid, scale #${primary.id}`, ps.currentDrop, runningData.currentDrop, ps.uid, runningData.uid);

      if (ps.uid === runningData.uid && ps.currentDrop === runningData.currentDrop) {
        this.dischargeScale({ ...primary, isPrimary: true }, runningData);
      }

      return;
    }

    if (primary) {
      this.convertWater(runningData);
      logger.log('primary scale exist, discharging per sequence:', runningData.sequence);

      const ps = this.getDeviceStatus(primary.id);
      logger.log(`checking drop & uid, scale #${primary.id}`, ps.currentDrop, runningData.currentDrop, ps.uid, runningData.uid);

      if (ps.uid === runningData.uid && ps.currentDrop === runningData.currentDrop) {
        this.dischargeScale(primary, runningData, (primary) => {
          this.onPrimaryDischarge(primary, runningData);
        });
      }

    } else {
      logger.log('no primary scale! discharging all at once', runningData.finalScales, runningData.sequence.discharges);
      const waterScale = runningData.sequence.discharges.find((s: DischargeScale) => s.type === ScaleTypes.WATER);

      secondaries.forEach((s: DischargeScale) => {
        const ds = this.getDeviceStatus(s.id);
        logger.log(`checking drop & uid, scale #${s.id}`, ds.currentDrop, runningData.currentDrop, ds.uid,  runningData.uid);

        if (ds.uid === runningData.uid && ds.currentDrop === runningData.currentDrop) {
          if (waterScale) {
            if (!waterScale.dischargeFinish && !s.withWater) {
              logger.log('scale without water, waiting when water finish', s);
            } else {
              this.dischargeScale(s, runningData);
            }
          } else {
            this.dischargeScale(s, runningData);
          }
        }
      });

      this.onBatchAction(runningData, BatchAction.StartDischarging);
      this.onBatchAction(runningData, BatchAction.StartDischargingHeadWater);
      this.onBatchAction(runningData, BatchAction.StartDischargingWashWater);
    }
  },

  areAllPumpsFinished(this: IBatchStore, runningData: IBatchData) {

    const pumpsFinish: string[] = [];

    if (runningData?.pumps) {
      Object.keys(runningData.pumps).forEach(pumpId => {
        if (runningData.pumps[pumpId].isDischarged) {
          pumpsFinish.push(pumpId);
        }
      });

      const allFinish = Object.keys(runningData.pumps).length === pumpsFinish.length;
      logger.log('check all pumps finish discharge:', allFinish, runningData.uid,
        Object.keys(runningData.pumps), pumpsFinish);

      return allFinish;
    }

    return true;
  },

  checkPumpsFinish(this: IBatchStore, runningData: IBatchData) {

    if (runningData?.pumps) {
      this.timers.clear(timerKey.checkAdmixturesFinish());

      if (this.areAllPumpsFinished(runningData)) {
        this.dischargeMixer(runningData);
      } else {
        this.timers.set(timerKey.checkAdmixturesFinish(), () => {
          this.checkPumpsFinish(runningData);
        }, 1000);

      }
    } else {
      this.dischargeMixer(runningData);
    }

  },

  getPrimaryCumulative(this: IBatchStore, primary: DischargeScale | IScale | any, runningData: IBatchData) {

    const primaries = runningData.sequence.discharges.filter((v: DischargeScale) => v.isPrimary);
    const primaryScaleStatus = this.getDeviceStatus(primary.scaleId);
    const weight = this.getScaleWeight(primary);
    let primaryCumulativeTarget = 0;
    let primaryCumulativeWeight = 0;

    if (primaries.length === 1) {
      primaryCumulativeTarget = primary.decumulative && runningData.scales[primary.id]
        ? runningData.scales[primary.id].map((v: IWeighedDataToBatch) => v.mft).reduce((acc: any, v: any) => acc + v, 0)
        : primaryScaleStatus.target;
      primaryCumulativeWeight = primary.decumulative ? primaryCumulativeTarget - (primaryScaleStatus.startWeight - weight) : weight;
    } else if (primaries.length > 1) {
      primaries.forEach((v: DischargeScale) => {
        const target = runningData.scales[v.scaleId]
          // previouslyFedTarget keeps completed targets of a substitute that was reopened for more material.
          ? runningData.scales[v.scaleId]
            .map((v: IWeighedDataToBatch) => v.mft + (v.previouslyFedTarget || 0))
            .reduce((acc: any, v: any) => acc + v, 0)
          : primaryScaleStatus.target;
        const scale = this.devicesStore.getDeviceById(v.scaleId);
        primaryCumulativeTarget += target;
        primaryCumulativeWeight += this.getScaleWeight(scale);
      });
    }

    return {
      target: primaryCumulativeTarget,
      weight: primaryCumulativeWeight,
    };
  },

  checkIfWashWaterReadyFromSecondary(this: IBatchStore, runningData: IBatchData) {

    const primaryWater = runningData.sequence.discharges.find((v: DischargeScale) => v.isPrimary && v.type === ScaleTypes.WATER);
    const secondaries = runningData.sequence.discharges.filter((v: DischargeScale) => !v.isPrimary && v.washStart);

    let readyScales = [];

    secondaries.forEach((scale: DischargeScale) => {
      if (scale.dischargeFinish) {
        readyScales.push(scale);
      }
      logger.log('discharge wash water can start?', scale.type, !!scale.dischargeFinish);
    });

    if (secondaries.length === readyScales.length) {
      this.dischargeScale(primaryWater, runningData);
      return;
    }

    this.timers.set(timerKey.device(primaryWater.id), () => {
      this.checkIfWashWaterReadyFromSecondary(runningData);
    }, 1000);
  },

  onPrimaryDischarge(this: IBatchStore, primary: DischargeScale, runningData: IBatchData) {

    if (!runningData) {
      return;
    }

    const waterScale = runningData.sequence.discharges.find((v: DischargeScale) => v.type === ScaleTypes.WATER);
    const wp = getWaterPump(this.rootStore, runningData);
    const waterPump = wp?.id && runningData.pumps ? runningData.pumps[wp.id] : null;
    const primaryCumulative = this.getPrimaryCumulative(primary, runningData);
    const primaryShouldStop = waterScale && runningData.finalScales[waterScale.id] && !primary.withWater && !primary.dischargeIsStop && waterScale.dischargeStarted
      && !waterScale.dischargeIsStop && !waterScale.dischargeFinish;
    const primaryShouldStopForHeadWater = runningData.headWater > 0
      && primary.startAtHeadWater > 0
      && !primary.startAtHeadWaterReady
      && isHeadWaterNotDischarged(this.rootStore, runningData)
      && !primary.dischargeIsStop
      && primary.type !== ScaleTypes.WATER;
    const secondaries = runningData.sequence.discharges.filter((v: DischargeScale) => !v.isPrimary);
    const isSecondaryWithoutWater = !!secondaries.filter((v: DischargeScale) => !v.withWater && !v.dischargeFinish).length;
    const isWashWaterStartFromSecondary = !!secondaries.filter((v: DischargeScale) => v.washStart > 0 && !v.dischargeFinish).length;
    const primaryWaterShouldStop = primary.type === ScaleTypes.WATER && isWashWaterStartFromSecondary && primaryCumulative.weight <= runningData.washWater;

    // Hold & resume logic for "scale to wait for"
    const scaleToWaitFor = runningData.sequence.discharges.find((v: DischargeScale) => v.scaleId === primary.holdForScaleId);
    const realHoldAmount = primary.holdAmountType === DischargeAmountType.Unit ? primary.holdAmount : primaryCumulative.target * (primary.holdAmount/100);

    let primaryAggShouldStopForAnotherScale = false;
    let primaryAggShouldResumeForAnotherScale = false;
    if (scaleToWaitFor) {
      const realResumeAmount = primary.resumeAmountType === DischargeAmountType.Unit ? primary.resumeAmount : scaleToWaitFor.target - (scaleToWaitFor.target * (primary.resumeAmount / 100));
      const scaleToWaitForWeight = scaleToWaitFor ? this.getDeviceStatus(scaleToWaitFor.scaleId).weight : 0;
      primaryAggShouldStopForAnotherScale = primary.type === ScaleTypes.AGG
        && primary.holdForScaleId
        && !primary.dischargeIsStop
        && !scaleToWaitFor.dischargeFinish
        && !primary.dischargeResumedAfterStop
        && primaryCumulative.weight <= realHoldAmount;
      primaryAggShouldResumeForAnotherScale = primary.type === ScaleTypes.AGG && primary.holdForScaleId && primary.dischargeIsStop && !primary.dischargeResumedAfterStop && scaleToWaitFor && scaleToWaitForWeight <= realResumeAmount;

      logger.log('hold primary scale for another scale', primaryAggShouldStopForAnotherScale, primary.type, realHoldAmount, primaryCumulative.weight,
        primary.holdForScaleId,
        !primary.dischargeIsStop,
        !scaleToWaitFor.dischargeFinish,
        !primary.dischargeResumedAfterStop);
    }

    logger.log('discharge primary water should stop?', primary.type, primaryWaterShouldStop, isWashWaterStartFromSecondary,
      primaryCumulative.weight, runningData.washWater);

    const shouldStopPrimary = primaryShouldStop || primaryWaterShouldStop || primaryShouldStopForHeadWater || primaryAggShouldStopForAnotherScale;
    logger.log('discharge: should stop primary?', primaryShouldStop, primaryWaterShouldStop, primaryShouldStopForHeadWater, primaryAggShouldStopForAnotherScale);

    if (shouldStopPrimary && primary) {
      logger.log(`hold primary for cement scale ${primaryAggShouldStopForAnotherScale}; real hold amount: ${realHoldAmount}`);
      this.pauseDischargeScale(primary, primaryAggShouldStopForAnotherScale ? DeviceStatus.WAIT_DISCH : DeviceStatus.WAIT_WATER);

      primary.dischargeIsStop = true;
      this.updateScaleDischarge(primary, runningData);
      logger.log('discharge stop primary', primary.id, primary.type);

      if (primaryWaterShouldStop) {
        this.checkIfWashWaterReadyFromSecondary(runningData);
      }
    }
    if (primaryAggShouldResumeForAnotherScale) {
      logger.log(`primary agg should resume for another scale: ${primaryAggShouldResumeForAnotherScale}`);
      this.resumeDischargeScale(primary);
      primary.dischargeIsStop = false;
      primary.dischargeResumedAfterStop = true;
      this.updateScaleDischarge(primary, runningData);
      this.dischargeScale(primary, runningData, () => {
        this.onPrimaryDischarge(primary, runningData);
      });
    }

    if (primary.type === ScaleTypes.WATER) {
      this.onBatchAction(runningData, BatchAction.StartDischargingHeadWater);

      if (!runningData.washWater) {
        // case when is no wash water but we want to discharge material witch has
        // dischargeStart = StartDischargingWashWater
        this.onBatchAction(runningData, BatchAction.StartDischargingWashWater);
      }
    }

    if (!waterScale && waterPump && !waterPump.washWaterReadyForDischarge) {
      const targetMinusWeight = primaryCumulative.target - primaryCumulative.weight;
      const washWaterStartTarget = primaryCumulative.target * (primary.washStart / 100);
      const shouldWashWaterStart = primary.washStart <= 100 && waterPump.washWater > 0 && !isSecondaryWithoutWater
        && !isWashWaterStartFromSecondary && targetMinusWeight >= washWaterStartTarget
        && (primary.withWater || (!primary.withWater && primary.washStart < 100));

      logger.log('water pump should start wash water?', shouldWashWaterStart,
        targetMinusWeight, '>', washWaterStartTarget, primaryCumulative,
        'secondary without water and not finish', isSecondaryWithoutWater,
        'wash water:', waterPump.washWater, 'wash wait for secondary scale?', isWashWaterStartFromSecondary);

      if (shouldWashWaterStart) {
        waterPump.washWaterReadyForDischarge = true;
        logger.log('starting wash water for water pump:', waterPump.washWater);
        logger.log(`water for pump must be set as follow: Discharge Start: ${BatchAction.StartDischargingWashWater} !!!`);
        this.onBatchAction(runningData, BatchAction.StartDischargingWashWater);
      }
    }

    secondaries.forEach((secondary: DischargeScale | any, i: number) => {
      secondary = { ...secondary, ...runningData.sequence.discharges.find((v: DischargeScale) => v.scaleId === secondary.scaleId) };
      const target = Math.max(primaryCumulative.target - primaryCumulative.weight, 0);
      const secondaryTarget = primaryCumulative.target * (secondary.start / 100);
      const isPercentCorrect = (target >= secondaryTarget) || (primary.startAtHeadWater > 0 && secondary.type === ScaleTypes.WATER);
      logger.log(`secondary #${secondary.id} ${secondary.type} target: ${secondaryTarget}, primary #${primary.id} weight: ${target}`,
        'is percent correct: ', isPercentCorrect, primaryCumulative.target, primaryCumulative.weight, secondary.start);

      if (runningData.finalScales[secondary.scaleId] && isPercentCorrect && !runningData.finalScalesDischarged.includes(secondary.id)) {

        if (secondary.type === ScaleTypes.WATER) {
          // water
          const waterWeight = this.getScaleWeight(secondary);
          const isWashWaterAvailable = runningData.washWater > 0;
          const isDischargeBlocked = secondary.dischargeIsStop || !runningData.headWater;
          const shouldWashWaterStart = !isWashWaterStartFromSecondary
            && (primary?.withWater || (!primary?.withWater && primary?.washStart < 100))
            && (
              runningData.startWashWaterManually
              ||
              (primaryCumulative.target - primaryCumulative.weight >= primaryCumulative.target * (primary.washStart / 100))
            )
            && !this.isHoldScale(secondary.scaleId)
            && this.isDischargeAdmixBeforeWashWaterCompleted(runningData);
          const waterShouldStop = isWashWaterAvailable
            && waterWeight <= runningData.washWater
            && !secondary.dischargeIsStop
            && !secondary.dischargeFinish
            && !secondary.dischargeIsResume;
          const washWaterShouldWait = isWashWaterAvailable
            && !shouldWashWaterStart
            && isDischargeBlocked
            && !secondary.dischargeFinish
            && !primary.dischargeFinish
            && !runningData.startWashWaterManually;
          const washWaterShouldResume = isWashWaterAvailable
            && shouldWashWaterStart
            && isDischargeBlocked
            && !secondary.dischargeFinish
            && !secondary.dischargeIsResume
            && !this.isHoldScale(secondary.scaleId)
            && (primary?.withWater || (!primary?.withWater && primary?.washStart < 100));

          logger.log('waterShouldStop', waterShouldStop, waterWeight, 'shouldWashWaterStart', shouldWashWaterStart,
            'washWaterShouldResume', washWaterShouldResume, 'washWaterShouldWait', washWaterShouldWait);

          if (!runningData.headWater && !shouldWashWaterStart) {
            logger.log('discharge no head water, waiting for wash water', secondary.id, secondary.type);
            return;
          }

          if (waterShouldStop && !shouldWashWaterStart) {
            // TODO: There's similar logic in onSecondaryScaleDischarge. Investigate for a potential refactor.
            this.pauseDischargeScale(secondaries[i]);
            secondaries[i].dischargeIsStop = true;
            secondaries[i].dischargeStarted = false;
            this.updateScaleDischarge(secondaries[i], runningData);
            logger.log('discharge pause water', secondary.id, secondary.type);

            // At this point we know head water was discharged
            runningData.headWaterScaleDischarged = true;
            return;
          }

          if (washWaterShouldWait) {
            logger.log('discharge wait wash water', secondary.id, secondary.type);
            return;
          }

          if (washWaterShouldResume) {
            this.resumeDischargeScale(secondary);
            secondaries[i].dischargeIsResume = true;
            secondaries[i].dischargeStarted = false;
            this.updateScaleDischarge(secondaries[i], runningData);
            logger.log('discharge resume water', secondary.id, secondary.type);
            this.onBatchAction(runningData, BatchAction.StartDischargingWashWater);
          }

        } else {
          // When WATER start before CEM or AGG and w/water is unchecked -
          // WATER needs to finish first and than CEM or AGG can start
          const scaleShouldStop = !secondary.withWater && !secondary.dischargeIsStop
            && (
              (waterScale && waterScale.dischargeStarted && !waterScale.dischargeFinish && !waterScale.dischargeIsStop)
              || (!waterScale && waterPump && waterPump.headWater && !waterPump.headWaterDischarged)
              || (waterScale && !waterScale.dischargeIsStop && primary.dischargeIsStop)
            );
          const scaleShouldWait = !secondary.withWater
            && (
              (waterScale && waterScale.dischargeStarted && !waterScale.dischargeFinish && !waterScale.dischargeIsStop)
              || (!waterScale && waterPump && waterPump.headWater && !waterPump.headWaterDischarged)
            );
          const scaleShouldResume = !scaleShouldWait && secondary.dischargeIsStop && !secondary.dischargeIsResume;
          logger.log('discharge secondary:', secondary.id, secondary.type, 'scaleShouldStop:', scaleShouldStop,
            'scaleShouldWait:', scaleShouldWait, 'scaleShouldResume:', scaleShouldResume, secondary, waterScale, waterPump);

          if (scaleShouldStop) {
            this.pauseDischargeScale(secondaries[i]);
            secondaries[i].dischargeIsStop = true;
            this.updateScaleDischarge(secondaries[i], runningData);
            logger.log('discharge pause', secondary.id, secondary.type);
            return;
          }

          if (scaleShouldWait) {
            logger.log('discharge wait', secondary.id, secondary.type, 'waterScale', waterScale);
            return;
          }

          if (scaleShouldResume) {
            this.resumeDischargeScale(secondary);
            secondaries[i].dischargeIsResume = true;
            secondaries[i].dischargeStarted = false;
            this.updateScaleDischarge(secondaries[i], runningData);
            logger.log('discharge resume', secondary.id, secondary.type);
          }

        }

        if (!secondaries[i].dischargeStarted) {
          logger.log('discharge scale ok:', secondary.id, secondary.type, secondary.start);
          secondaries[i].dischargeStarted = true;
          this.updateScaleDischarge(secondaries[i], runningData);

          if (secondary.type === ScaleTypes.WATER) {
            this.onBatchAction(runningData, BatchAction.StartDischargingHeadWater);

            if (!runningData.washWater) {
              // case when is no wash water but we want to discharge material witch has
              // dischargeStart = StartDischargingWashWater
              this.onBatchAction(runningData, BatchAction.StartDischargingWashWater);
            }
          }

          this.dischargeScale(secondary, runningData, (scale) => {
            this.onSecondaryScaleDischarge(scale, primary, runningData);
          });
        }
      }
    });

  },

  onSecondaryScaleDischarge(this: IBatchStore, scale: DischargeScale | any, primary: DischargeScale, runningData: IBatchData) {

    primary = runningData.sequence.discharges.find((v: DischargeScale) => v.scaleId === primary.scaleId);
    scale = runningData.sequence.discharges.find((v: DischargeScale) => v.scaleId === scale.scaleId);
    const scaleWeight = this.getScaleWeight(scale);
    logger.log('onSecondaryScaleDischarge:', scale.id, scale.type, scaleWeight);

    if (runningData && scale.type === ScaleTypes.WATER) {
      const waterShouldStop = primary && !primary.dischargeFinish
        && runningData.washWater > 0 && scaleWeight <= runningData.washWater
        && !scale.dischargeIsStop && !scale.dischargeFinish && !primary.withWater;
      const s = this.getDeviceStatus(scale.id);

      if (primary && s.target &&  primary.startAtHeadWater > 0 && !primary.dischargeIsResume) {
        const actualPercent = s.target > scaleWeight ? toNumber(((s.target - scaleWeight) / s.target) * 100) : 0;

        logger.log('check percent for startAtHeadWater', primary.type, primary.startAtHeadWater, actualPercent);

        if (actualPercent >= primary.startAtHeadWater) {
          primary.startAtHeadWaterReady = true;
          this.updateScaleDischarge(primary, runningData);
          this.resumePrimaryScaleDischarge(runningData);
          logger.log('percent ok for startAtHeadWater, resume discharge scale', primary.type, actualPercent);
        }
      }

      if (waterShouldStop) {
        this.pauseDischargeScale(scale);
        scale.dischargeIsStop = true;
        this.updateScaleDischarge(scale, runningData);
        logger.log('discharge pause water', scale.id, scale.type);
        // case when finish head water and stop (waiting for wash water) and primary is stopped (withWater is
        // unchecked) and we want to resume primary
        const primaryShouldResume = !primary.withWater && primary.dischargeIsStop && !primary.dischargeFinish && !primary.dischargeIsResume;
        logger.log('onSecondaryScaleDischarge:', primaryShouldResume, !primary.withWater, primary.dischargeIsStop, !primary.dischargeFinish, !primary.dischargeIsResume, primary);

        // At this point, we can assume head water has been successfully discharged,
        // as there's only wash water remaining.
        runningData.headWaterScaleDischarged = true;

        if (primaryShouldResume) {
          logger.log('discharge resume primary after water pause:', primary.id, primary.type);
          this.resumeDischargeScale(primary);
          primary.dischargeIsResume = true;
          this.updateScaleDischarge(primary, runningData);

          this.dischargeScale(primary, runningData, () => {
            this.onPrimaryDischarge(primary, runningData);
          });
        }

      }

    }
  },

  dischargeWaterIfReady(this: IBatchStore, runningData: IBatchData) {

    const w = this.waitingForDischarge.filter(v => v.uid === runningData.uid && v.currentDrop === runningData.currentDrop);

    if (this.isAbort || !this.isActiveDrop(runningData) || !runningData || runningData.materialId || !this.isDischarge
      || (runningData.finalScales && w.length === Object.keys(runningData.finalScales).length)) {
      this.timers.clear(timerKey.dischargeWater());
      return;
    }

    const sequence = runningData.sequence;
    let waterScale = sequence.discharges.find((v: DischargeScale) => v.type === ScaleTypes.WATER);
    const ds = waterScale ? this.getDeviceStatus(waterScale.scaleId) : null;
    const waterPump = getWaterPump(this.rootStore, runningData) || getSecondWaterPump(this.rootStore, runningData);
    logger.log('discharge water first:', waterScale, waterPump);

    if (waterPump) {
      if (runningData.mixerId && !this.mixerIsReadyForDischarge(runningData)) {
        this.timers.clear(timerKey.dischargeWater());
        this.timers.set(timerKey.dischargeWater(), () => {
          this.dischargeWaterIfReady(runningData);
        }, 2000);
        return;
      }

      const ps = this.getDeviceStatus(waterPump.id);
      const ts = this.getDeviceStatus(waterPump.tankId);

      if (ps.status === DeviceStatus.WAIT_DISCH || ts.status === DeviceStatus.WAIT_DISCH) {
        this.onBatchAction(runningData, BatchAction.NoWait);
      }

      if (!waterPump.tankId && ps.status === DeviceStatus.IDLE) {
        this.onBatchAction(runningData, BatchAction.StartDischarging);
      }

      if (!runningData.finalScales) {
        this.dischargeWithSequence(runningData);
      }
    }

    if (waterScale?.noWait && !runningData.finalScalesDischarged.includes(waterScale.scaleId)
      && ds && ds.status !== DeviceStatus.FINISH && runningData.finalScales[waterScale.scaleId]) {
      this.timers.clear(timerKey.dischargeWater());

      if (runningData.mixerId && !this.mixerIsReadyForDischarge(runningData)) {
        this.timers.set(timerKey.dischargeWater(), () => {
          this.dischargeWaterIfReady(runningData);
        }, 2000);
        return;
      }

      if (ds.status === DeviceStatus.WAIT_DISCH) {
        if (runningData.mixerId) {
          const mixer = this.devicesStore.getDeviceById(Number(runningData.mixerId));
          if (mixer) {
            const { uid, currentDrop, allDrops, tId, truckCode } = runningData;

            const temperWaterData = this.getTemperWaterData(mixer);
            runningData.temperWaterStart = this.getTemperWater(mixer) * temperWaterData.meterRate;
            runningData.temperWaterUnitSymbol = temperWaterData.unitSymbol;
            runningData.temperWaterUnitId = temperWaterData.unitId;
            runningData.temperWaterPNo = mixer.tWaterMeterStatusPNo;
            logger.log('start temper water', runningData.temperWaterStart, runningData.temperWaterUnitSymbol, mixer.tWaterMeterStatusPNo);

            runningData.mixerFeedingStartTime = Date.now();
            logger.log('mixer feeding start', mixer.mixingStartPoint);

            this.deviceStatus.set(mixer.id, {
              ...this.getDeviceStatus(mixer.id),
              id: mixer.id,
              status: mixer.mixingStartPoint === MixingStartPoint.OnFirstMaterialLoaded ? DeviceStatus.START_MIXING : DeviceStatus.FEED,
              restart: true,
              delay: 0,
              uid,
              currentDrop,
              allDrops,
              tId,
              truckCode,
            });
          }
        }

        this.convertWater(runningData);
        waterScale = { ...waterScale, ...this.devicesStore.getDeviceById(waterScale.scaleId) };

        if (runningData.pumps && Object.keys(runningData.pumps).length) {
          this.onBatchAction(runningData, BatchAction.StartDischarging);
        }

        logger.log(',discharge scale ok:', waterScale.id, waterScale.type, waterScale.start);
        this.context.onBatchCycle(BatchCycleStatus.START_DISCHARGING, this.inputs, this.outputs, this.channels);
        waterScale.dischargeStarted = true;
        this.updateScaleDischarge(waterScale, runningData);

        this.dischargeScale(waterScale, runningData, (scale, reaction) => this.onWaterFirstDischargeChange(scale, runningData, reaction));
      } else {
        this.timers.set(timerKey.dischargeWater(), () => {
          this.dischargeWaterIfReady(runningData);
        }, 2000);
      }
    }
  },

  onWaterFirstDischargeChange(this: IBatchStore, waterScale: DischargeScale | any, runningData: IBatchData, reaction: any) {

    const waterWeight = this.getScaleWeight(waterScale);
    const primary = runningData.sequence.discharges.find((v: DischargeScale) => v.isPrimary && v.order === 1)
      || runningData.sequence.discharges.find((v: DischargeScale) => v.isPrimary);
    const primaryShouldResume = !!primary
      && !primary.withWater
      && primary.dischargeIsStop
      && !primary.dischargeFinish
      && !primary.dischargeIsResume;
    const waterShouldStop = primary && !primary.dischargeFinish && runningData.washWater > 0
      && waterWeight <= runningData.washWater;
    logger.log('onWaterFirstDischargeChange: waterShouldStop', waterShouldStop, runningData.washWater, waterWeight);

    if (runningData.headWater) {
      this.onBatchAction(runningData, BatchAction.StartDischarging);
      this.onBatchAction(runningData, BatchAction.StartDischargingHeadWater);
    }

    if (waterShouldStop) {
      this.pauseDischargeScale(waterScale);
      waterScale.dischargeIsStop = true;
      this.updateScaleDischarge(waterScale, runningData);
      logger.log('discharge pause water', waterScale.id, waterScale.type);

      if (primary?.dischargeIsStop) {
        logger.log('resume primary after water stop:', primary.type, primary.decumulative);
        if (primaryShouldResume && primary.decumulative) {
          this.resumePrimaryScaleDischarge(runningData);
        } else {
          this.dischargeWithSequence(runningData);
        }
      }

      if (reaction) {
        // finish observe water scale, will be continued with other scale discharge sequence
        reaction.dispose();
      }
    }

  },

  updateSequenceTargets(this: IBatchStore, runningData: IBatchData) {

    if (runningData.sequence) {
      // case when water discharging first and we need new target
      const s = this.getSequence(runningData);
      runningData.sequence?.discharges?.forEach((v: DischargeScale, i: number) => {
        runningData.sequence.discharges[i] = { ...v, ...s.discharges.find((d: DischargeScale) => d.scaleId === v.scaleId) };
      });
    } else {
      runningData.sequence = this.getSequence(runningData);
    }
  },

  checkDischargeAuthorizationInput(this: IBatchStore) {

    const { settingsStore } = this.rootStore;
    const { configuration }: any = settingsStore;
    const dischargeAuthorization = this.running?.length ? this.running[0].dischargeAuthorization : false;

    this.timers.clear(timerKey.dischargeAuthorization());

    if (
      configuration.dischargeAuthorizationInput
      && !dischargeAuthorization
      && this.isDischarge
    ) {
      logger.log(`DISCHARGE: waiting for authorization input: ${configuration.dischargeAuthorizationInput}`);
      this.showWarning('Discharge is waiting for authorization');

      this.timers.set(timerKey.dischargeAuthorization(), () => {
        if (this.isDischarge) {
          this.checkDischargeAuthorizationInput();
        }
      }, 2000);
    } else {
      this.discharge();
    }
  },

  isMultiBatchMixerReadyForAnotherDrop(this: IBatchStore, runningData: IBatchData) {

    if (this.running.length > 1 && runningData?.mixerId) {
      const mixer = this.devicesStore.getDeviceById(Number(runningData.mixerId));
      if (mixer?.multibatch) {
        const ms = this.getDeviceStatus(runningData.mixerId);

        if (
          runningData.uid === ms.uid
          && runningData.currentDrop === ms.currentDrop
          && runningData.allDrops !== ms.currentDrop
          && ms.status === DeviceStatus.MIXING
        ) {
          logger.log(`multibatch mixer: ${mixer.description} ${ms.status}
          ready for another drop ${ms.currentDrop+1}/${runningData.allDrops}`);
          return true;
        }
      }
    }

    return false;
  },

  discharge(this: IBatchStore, manual = false) {
    return _discharge(this, manual);
  },

  getDecumTarget(this: IBatchStore, scaleId: number, initialData: IBatchData) {

    let target = 0;

    if (!initialData.scales?.[scaleId]) {
      return target;
    }

    initialData.scales[scaleId].forEach((v: IWeighedDataToBatch) => {
      target += v.mft;
    });

    return target;
  },

  getDecumTolerance(this: IBatchStore, scale: IScale) {

    let tolerance = 0;

    if (!scale.decumulative) {
      return tolerance;
    }

    const materials = this.materialStore.materials.filter((v: IWeighedMaterialModel) => v.scaleId === scale.id);

    materials.forEach((material: IMaterial) => {
      const s = this.getDeviceStatus(`${scale.id}-${material.code}`);
      if (s.target) {
        const materialDetails = this.materialStore.materialDetails[material.materialDetailId];
        tolerance += this.getTolerance(materialDetails, s.target)?.under;
      }
    });

    return tolerance;
  },

  checkScalesDischargeFinish(this: IBatchStore) {

    reaction(
      () => this.statusCount,
      () => {

        if (this.canEmit && this.waitingForDischarge.length && this.running.length) {
          this.devicesStore.finalScales.forEach((scale: IScale | any) => {
            let weight = this.getScaleWeight(scale);
            const s = this.getDeviceStatus(scale.id);

            if (!scale.decumulative
                && ((this.isScaleFinishedDischargeEarlier(scale.id) && s.status === DeviceStatus.FINISH)
                || (
                  ![DeviceStatus.IDLE, DeviceStatus.FINISH, DeviceStatus.SETTLING, DeviceStatus.CLOSE_DELAY, DeviceStatus.CLOSING].includes(s.status)
                  && weight <= (scale.dischargeTare || 0))
                )
            ) {
              const runningData = this.running[0];
              if (runningData.uid === s.uid && runningData.currentDrop === s.currentDrop
                && this.waitingForDischarge.find(v => v.scaleId === scale.id && v.uid === s.uid && v.currentDrop === s.currentDrop)) {
                logger.log('scale finish discharge', scale.id, weight, scale.type);
                this.onDischargeFinish(scale, runningData);
              }
            }

          });
        }

        // double gate mode - discharge second gate by percent
        if (this.canEmit && this.running.length) {
          this.devicesStore.scales.forEach((scale: IScale) => {
            let weight = this.getScaleWeight(scale);
            const s = this.getDeviceStatus(scale.id);

            if (this.isDoubleGateScale(scale) && scale.secondGateOpenDelayUnit === SecondGateOpenDelayUnit.Percent
              && (!s.secondGateInitialOpenSent || !s.firstGateInitialOpenSent)
              && s.status === DeviceStatus.DISCHARGING
            ) {
              const secondGateOpenDelay = scale.secondGateOpenDelay || 0;
              const runningData = this.running.find(v => v.uid === s.uid);
              const noFlowControl = scale.noFlowControl || runningData?.sequence?.noFlowControl;
              const initialOpenTime = this.getInitialOpenTimeForScale(scale, noFlowControl);
              const percentWeight = s.target - (s.target * (secondGateOpenDelay / 100));

              if (weight <= percentWeight) {
                switch (scale.doubleGatesOpenMode) {
                  case DoubleGatesOpenMode.OpenGateOneFirst:
                    this.openScale(scale, initialOpenTime, scale.openGateTwoPNo, scale.closeGateTwoPNo);
                    break;
                  case DoubleGatesOpenMode.OpenGateTwoFirst:
                    this.openScale(scale, initialOpenTime);
                    break;
                  default:
                  //none
                }

                logger.log(`open second gate ${scale.description} after ${secondGateOpenDelay}%`, percentWeight, weight, s.target);
                this.deviceStatus.set(scale.id, {
                  ...s,
                  firstGateInitialOpenSent: true,
                  secondGateInitialOpenSent: true,
                });
              }
            }
          });
        }

      },
    );
  },

  isScaleSwitchGatePortOn(this: IBatchStore, scale: IScale): boolean {
    return !!scale.switchGateTypePNo && this.isOutputOn(scale.switchGateTypePNo);
  },

};
